<template>
  <div class="partner-tags-select">
    <BittsSelectTags
      :model-value="labels"
      :options="options"
      :token-separators="[',']"
      placeholder="Type and press Enter to add a tag"
      @update:model-value="handleChange"
    />
    <BittsAlert
      v-if="hasLengthError"
      message="Tag cannot be created"
      color="warning"
      data-testid="partner-tags-select__length-error"
    >
      <template #body>
        <p>
          Partner tags cannot be greater than
          {{ MAX_PARTNER_TAG_LENGTH }} characters
        </p>
      </template>
    </BittsAlert>
  </div>
</template>

<script setup lang="ts">
import { BittsAlert, BittsSelectTags } from '@crossbeam/bitts';

import type { SelectValue } from 'ant-design-vue/es/select';
import { computed, ref } from 'vue';

import { MAX_PARTNER_TAG_LENGTH } from '@/constants/partners';

const { tags } = defineProps<{ tags: { label: string }[] }>();

const labels = defineModel<string[]>({ required: true });

const createdLabels = ref<string[]>([]);
const hasLengthError = ref(false);

const options = computed(() =>
  [...new Set([...tags.map((tag) => tag.label), ...createdLabels.value])]
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    .map((label) => ({ label, value: label })),
);

function handleChange(value: SelectValue) {
  const selectedLabels = Array.isArray(value) ? (value as string[]) : [];
  const validLabels = selectedLabels.filter(
    (label) => label.length <= MAX_PARTNER_TAG_LENGTH,
  );
  hasLengthError.value = validLabels.length !== selectedLabels.length;
  labels.value = validLabels;
  const knownLabels = new Set(options.value.map((option) => option.value));
  createdLabels.value.push(
    ...validLabels.filter((label) => !knownLabels.has(label)),
  );
}
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";
.partner-tags-select {
  @apply flex flex-col gap-16;
}
</style>
